import React from 'react';
import { motion } from 'framer-motion';
import { Check, BookOpen, Coffee, Zap, MessageCircle, Shield, Clock } from 'lucide-react';
export function SolutionSection() {
  const modules = [{
    icon: BookOpen,
    title: 'Why School Drains Kids',
    desc: 'Understand the “good all day, wild at home” pattern so you stop blaming yourself.'
  }, {
    icon: Clock,
    title: 'The 15-Minute Decompression Rule',
    desc: 'Exactly what to do (and not do) in the first moments after pickup/arrival.'
  }, {
    icon: Coffee,
    title: 'Snack Triggers You’re Missing',
    desc: 'Simple fixes to reduce blood-sugar/transition chaos—without turning snacks into a battle.'
  }, {
    icon: MessageCircle,
    title: 'Calm Scripts That Work',
    desc: 'Short phrases that de-escalate (not lecture) when their brain is offline.'
  }, {
    icon: Shield,
    title: 'Boundary Without Yelling',
    desc: 'How to hold the line without threats, long explanations, or emotional whiplash.'
  }, {
    icon: Zap,
    title: 'When to Ignore vs Intervene',
    desc: 'So you stop overcorrecting and start choosing the moves that actually work.'
  }];
  return <section className="py-24 px-4 md:px-6 bg-cream-100 relative" data-id="element-233">
      {/* Decorative blobs */}
      <div className="absolute top-20 left-0 w-64 h-64 bg-sage-light/20 rounded-full blur-3xl -translate-x-1/2" data-id="element-234" />
      <div className="absolute bottom-20 right-0 w-80 h-80 bg-dusty-blue/20 rounded-full blur-3xl translate-x-1/3" data-id="element-235" />

      <div className="max-w-6xl mx-auto relative z-10" data-id="element-236">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-20" data-id="element-237">
          <span className="text-terracotta font-bold tracking-wider text-sm uppercase mb-4 block" data-id="element-238">
            Introducing
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-sage-dark mb-6" data-id="element-239">
            Tantrum-Free After-School Evenings
          </h2>
          <p className="text-xl text-sage-dark/80 mb-8" data-id="element-240">
            A simple, emotionally safe guide for moms who want:
          </p>

          <div className="flex flex-wrap justify-center gap-3 md:gap-6 mb-10" data-id="element-241">
            {['calmer transitions', 'fewer meltdowns', 'boundaries without yelling', 'evening peace'].map((item, i) => <span key={i} className="inline-flex items-center px-4 py-2 bg-white rounded-full text-sage-dark font-medium shadow-sm border border-sage-light/30" data-id="element-242">
                <Check className="w-4 h-4 text-terracotta mr-2" data-id="element-243" />
                {item}
              </span>)}
          </div>

          <div className="bg-white p-6 rounded-xl border-l-4 border-terracotta text-left shadow-sm" data-id="element-244">
            <p className="text-sage-dark italic" data-id="element-245">
              <span className="font-bold not-italic" data-id="element-246">Note:</span> This is not a
              “perfect routine.” It’s a repeatable reset you can run even when
              you’re tired, behind on dinner, and your child is already
              spiraling.
            </p>
          </div>
        </div>

        {/* Modules Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 mb-24" data-id="element-247">
          {modules.map((module, i) => <motion.div key={i} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: i * 0.1
        }} className="bg-white p-8 rounded-2xl shadow-lg shadow-sage-dark/5 border border-sage-light/20 hover:border-terracotta/30 transition-colors group" data-id="element-248">
              <div className="w-12 h-12 bg-sage-light/30 rounded-full flex items-center justify-center text-sage-dark mb-6 group-hover:bg-terracotta/10 group-hover:text-terracotta transition-colors" data-id="element-249">
                <module.icon size={24} data-id="element-250" />
              </div>
              <h3 className="text-xl font-bold text-sage-dark mb-3" data-id="element-251">
                {module.title}
              </h3>
              <p className="text-sage-dark/70 leading-relaxed" data-id="element-252">{module.desc}</p>
            </motion.div>)}
        </div>

        {/* The Promise */}
        <motion.div initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} className="bg-sage-dark text-cream-50 rounded-3xl p-8 md:p-16 text-center relative overflow-hidden" data-id="element-253">
          <div className="absolute top-0 left-0 w-full h-full bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-5" data-id="element-254" />

          <div className="relative z-10 max-w-3xl mx-auto" data-id="element-255">
            <h3 className="text-3xl font-serif mb-6" data-id="element-256">The Promise</h3>
            <p className="text-lg md:text-xl leading-relaxed opacity-90 mb-8" data-id="element-257">
              This won’t make your child “never upset.” But it can help you
              dramatically reduce the daily after-school blowups—and more
              importantly: help you feel steady even when your child isn’t.
            </p>
            <p className="text-2xl font-bold text-sage-light" data-id="element-258">
              Because when you have a plan for 4PM, you stop reacting. You start
              leading.
            </p>
          </div>
        </motion.div>
      </div>
    </section>;
}